/**
 * Shopify Cross-Device Wishlist Sync Integrator
 * For logged-in customers: strictly stores & loads wishlist data from App Proxy (/apps/a/wishlist).
 * For guest customers: preserves existing localStorage flow without modification or merging.
 */
(function() {
  const PROXY_URL = "/apps/a/wishlist";
  const LOCAL_STORAGE_KEY = "wishlistItem";
  const ID_MAP_KEY = "wishlistProductIds";

  // Check if customer is logged in
  function isLoggedIn() {
    try {
      return !!(window.__customerId && String(window.__customerId).trim() !== "");
    } catch (e) {
      return false;
    }
  }

  // Get handles safely from localStorage
  function getLocalHandles() {
    try {
      const data = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!data) return [];
      const parsed = JSON.parse(data);
      if (!Array.isArray(parsed)) return [];
      return Array.from(new Set(parsed.filter(h => typeof h === 'string' && h.trim() !== '')));
    } catch (e) {
      return [];
    }
  }

  // Get ID map safely from localStorage
  function getIdMap() {
    try {
      const data = localStorage.getItem(ID_MAP_KEY);
      if (!data) return {};
      const parsed = JSON.parse(data);
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  // Save handles to localStorage safely and update theme UI/badges
  function saveLocalHandles(handles) {
    try {
      const safeHandles = Array.isArray(handles) 
        ? Array.from(new Set(handles.filter(h => typeof h === 'string' && h.trim() !== '')))
        : [];
      
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(safeHandles));
      } catch (storageErr) {
        console.warn("[Wishlist Sync] Could not write to localStorage:", storageErr);
      }
      
      // Update count display in theme
      const countElements = document.querySelectorAll('[data-wishlist-count]');
      countElements.forEach(el => {
        if (el) el.textContent = safeHandles.length;
      });

      // Fire update events for the theme
      const updateEvent = new CustomEvent('updatewishlistmail', { bubbles: true });
      document.dispatchEvent(updateEvent);
    } catch (e) {
      console.error("[Wishlist Sync] Error in saveLocalHandles:", e);
    }
  }

  // Debounced sync function to push current handles to the server for logged-in customer
  let debounceTimeout = null;
  function syncToServer(replace = false) {
    if (!isLoggedIn()) return;

    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(async () => {
      try {
        const handles = getLocalHandles();
        const idMap = getIdMap();
        
        const payload = {
          items: handles.map(handle => {
            let rawId = idMap[handle] ? String(idMap[handle]) : "0";
            let gid = (rawId && rawId !== "0" && !rawId.includes("gid://")) 
              ? `gid://shopify/Product/${rawId}` 
              : rawId;

            return {
              product_id: gid,
              handle: String(handle),
              added_at: new Date().toISOString()
            };
          }),
          replace: Boolean(replace)
        };

        const response = await fetch(PROXY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          console.log("[Wishlist Sync] Successfully saved wishlist to App Proxy.");
        } else {
          console.warn("[Wishlist Sync] App Proxy returned non-OK status:", response.status);
        }
      } catch (err) {
        console.error("[Wishlist Sync] Failed to save to App Proxy:", err);
      }
    }, 400);
  }

  // Sync strictly from server on page load for logged-in customer
  async function syncFromServer() {
    if (!isLoggedIn()) return;

    try {
      const response = await fetch(PROXY_URL, {
        headers: { "Accept": "application/json" }
      });
      if (!response.ok) return;

      const data = await response.json();
      if (!data || typeof data !== 'object') return;

      const items = Array.isArray(data.items) ? data.items : [];
      const serverHandles = items
        .map(item => item && typeof item.handle === 'string' ? item.handle.trim() : null)
        .filter(Boolean);

      // Update local ID map with IDs returned by the server
      const idMap = getIdMap();
      items.forEach(item => {
        if (item && item.handle && item.product_id && item.product_id !== "0") {
          const pidStr = String(item.product_id);
          const matches = pidStr.match(/\/Product\/(\d+)/);
          idMap[item.handle] = matches ? matches[1] : pidStr;
        }
      });
      
      try {
        localStorage.setItem(ID_MAP_KEY, JSON.stringify(idMap));
      } catch (e) {}

      // Overwrite local handles strictly with server handles (NO merging with guest local storage)
      saveLocalHandles(serverHandles);
      
      // Update heart icon states strictly for server handles
      updateHeartIcons(serverHandles);

    } catch (err) {
      console.error("[Wishlist Sync] Error fetching from App Proxy:", err);
    }
  }

  // Helper to update active wishlist heart buttons based strictly on logged-in server data
  function updateHeartIcons(handles) {
    try {
      const safeHandles = Array.isArray(handles) ? handles : [];
      const handlesSet = new Set(safeHandles);
      const elements = document.querySelectorAll('[data-wishlist]');
      
      elements.forEach(el => {
        if (!el) return;
        const handle = el.dataset.wishlistHandle || el.getAttribute('data-wishlist-handle');
        const textEl = el.querySelector('.text');
        
        if (handle && handlesSet.has(handle)) {
          el.classList.add('wishlist-added');
          if (textEl && window.wishlist && window.wishlist.added) {
            textEl.textContent = window.wishlist.added;
          }
        } else {
          el.classList.remove('wishlist-added');
          if (textEl && window.wishlist && window.wishlist.add) {
            textEl.textContent = window.wishlist.add;
          }
        }
      });
    } catch (e) {
      console.error("[Wishlist Sync] Error in updateHeartIcons:", e);
    }
  }

  // Listen in CAPTURING phase for clicks on wishlist heart buttons AND save-for-later buttons
  document.addEventListener('click', function(event) {
    if (!isLoggedIn()) return;

    try {
      const target = event.target ? (event.target.closest('[data-wishlist]') || event.target.closest('[data-save-for-later]')) : null;
      if (!target) return;

      const handle = target.dataset.wishlistHandle || target.getAttribute('data-wishlist-handle');
      const id = target.dataset.productId || target.getAttribute('data-product-id');

      if (handle && id) {
        const idMap = getIdMap();
        idMap[handle] = String(id);
        try {
          localStorage.setItem(ID_MAP_KEY, JSON.stringify(idMap));
        } catch (e) {}
      }

      // Allow theme.js / cart.js to update localStorage first, then push to App Proxy
      setTimeout(() => {
        const isDelete = target.classList ? !target.classList.contains('wishlist-added') : false;
        syncToServer(isDelete);
      }, 200);
    } catch (e) {
      console.error("[Wishlist Sync] Click listener error:", e);
    }
  }, true);

  // Listen for custom theme events when wishlist items are added
  document.addEventListener('addwishlistitem', function(e) {
    if (!isLoggedIn()) return;
    try {
      const handle = e.detail?.handle;
      if (handle) {
        setTimeout(() => {
          syncToServer(false);
        }, 200);
      }
    } catch (err) {}
  });

  // Initialize on DOM load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFromServer);
  } else {
    syncFromServer();
  }
})();
